import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { FaSave, FaSync } from "react-icons/fa";
import { toast } from "react-toastify";
import { get, put, post } from "../../utils/makeRequest";

const CUSTOMS_TABLE_FIELDS = [
  "Currency Code",
  "Currency Name",
  "Units of foreign currency equivalent to Indian rupees",
  "Import Rate",
  "Export Rate",
];

const CUSTOMS_EFFECTIVE_DATE_FIELD = "Effective start Date";

const RBI_FIELDS = [
  "date",
  "USD/INR",
  "GBP/INR",
  "EUR/INR",
  "JPY/INR",
  "AED/INR",
  "IDR/INR",
];

const emptyCustomsRow = () =>
  Object.fromEntries(CUSTOMS_TABLE_FIELDS.map((k) => [k, ""]));

const emptyRbiRow = () =>
  Object.fromEntries(RBI_FIELDS.map((k) => [k, ""]));

const pickCustomsEffectiveStartDate = (data = []) => {
  for (const row of data) {
    const value = row?.[CUSTOMS_EFFECTIVE_DATE_FIELD];
    if (value != null && String(value).trim()) return String(value).trim();
  }
  return "";
};

/**
 * Shared admin editor for Indian Customs / RBI latest.
 * kind: "customs" | "rbi"
 */
const ReferenceRatesAdminTab = ({ kind }) => {
  const isCustoms = kind === "customs";
  const title = isCustoms ? "Indian Customs Rates" : "RBI Reference Rates";
  const endpoint = isCustoms
    ? "/admin/reference-rates/indian-customs"
    : "/admin/reference-rates/rbi-latest";
  const refreshEndpoint = `${endpoint}/refresh`;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [mode, setMode] = useState("auto");
  const [rows, setRows] = useState([]);
  const [effectiveStartDate, setEffectiveStartDate] = useState("");
  const [cachedAt, setCachedAt] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [error, setError] = useState(null);

  const fields = isCustoms ? CUSTOMS_TABLE_FIELDS : RBI_FIELDS;
  const readOnly = mode === "auto";

  const applyPayload = (payload) => {
    if (isCustoms) {
      const data = Array.isArray(payload?.data) ? payload.data : [];
      setEffectiveStartDate(pickCustomsEffectiveStartDate(data));
      setRows(
        data.length
          ? data.map((r) => {
              const next = { ...emptyCustomsRow(), ...r };
              delete next[CUSTOMS_EFFECTIVE_DATE_FIELD];
              return next;
            })
          : [emptyCustomsRow()]
      );
    } else {
      const row = payload?.data?.[0] || emptyRbiRow();
      setRows([{ ...emptyRbiRow(), ...row }]);
    }
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await get(endpoint);
      setMode(res?.mode === "manual" ? "manual" : "auto");
      applyPayload(res?.payload);
      setCachedAt(res?.cached_at || null);
      setUpdatedAt(res?.updated_at || null);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || `Failed to load ${title}`);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, title, isCustoms]);

  useEffect(() => {
    load();
  }, [load]);

  const updateCell = (rowIndex, field, value) => {
    if (readOnly) return;
    setRows((prev) =>
      prev.map((row, i) => (i === rowIndex ? { ...row, [field]: value } : row))
    );
  };

  const addCustomsRow = () => {
    if (readOnly || !isCustoms) return;
    setRows((prev) => [...prev, emptyCustomsRow()]);
  };

  const removeCustomsRow = (index) => {
    if (readOnly || !isCustoms) return;
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const buildPayload = () => {
    if (isCustoms) {
      const sharedDate = String(effectiveStartDate || "").trim();
      const data = rows.map((row) => {
        const next = { ...emptyCustomsRow(), ...row };
        if (sharedDate) next[CUSTOMS_EFFECTIVE_DATE_FIELD] = sharedDate;
        else delete next[CUSTOMS_EFFECTIVE_DATE_FIELD];
        return next;
      });
      return {
        meta: { row_count: data.length },
        data,
      };
    }
    return { data: [rows[0] || emptyRbiRow()] };
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      const body =
        mode === "manual"
          ? { mode: "manual", payload: buildPayload() }
          : { mode: "auto" };
      await put(endpoint, body);
      toast.success(
        mode === "manual"
          ? "Saved to Redis + database (manual mode)"
          : "Switched to auto — upstream refresh started"
      );
      await load();
    } catch (err) {
      const msg = err.response?.data?.error || "Save failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleModeChange = (next) => {
    setMode(next);
  };

  const handleRefresh = async () => {
    if (mode === "manual") {
      toast.info("Switch to Auto mode to refresh from upstream");
      return;
    }
    try {
      setRefreshing(true);
      await post(refreshEndpoint, {});
      toast.success("Upstream refresh started");
      setTimeout(load, 2500);
    } catch (err) {
      toast.error(err.response?.data?.error || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <Container fluid className="py-4 text-center">
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container fluid className="py-3">
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
        <div>
          <h2 className="mb-1">{title}</h2>
          <p className="text-muted mb-0">
            {isCustoms
              ? "Public API: GET /api/forex/reference-rates/indian-customs"
              : "Public API: GET /api/forex/reference-rates/rbi-latest (JPY/INR per 100 JPY; IDR/INR per 10,000 IDR)"}
          </p>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing || mode === "manual"}
          >
            {refreshing ? (
              <Spinner size="sm" className="me-1" />
            ) : (
              <FaSync className="me-1" />
            )}
            Refresh upstream
          </Button>
          <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
            {saving ? <Spinner size="sm" className="me-1" /> : <FaSave className="me-1" />}
            Save
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card className="shadow-sm mb-3">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Mode</Form.Label>
                <Form.Select
                  value={mode}
                  onChange={(e) => handleModeChange(e.target.value)}
                >
                  <option value="auto">Auto (upstream)</option>
                  <option value="manual">Manual (admin edit)</option>
                </Form.Select>
              </Form.Group>
            </Col>
            {isCustoms && (
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Effective start Date</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="YYYY-MM-DD"
                    value={effectiveStartDate}
                    onChange={(e) => setEffectiveStartDate(e.target.value)}
                    readOnly={readOnly}
                    className={readOnly ? "bg-light" : ""}
                  />
                </Form.Group>
              </Col>
            )}
            <Col md={isCustoms ? 6 : 9}>
              <div className="d-flex flex-wrap gap-2 align-items-center">
                <Badge bg={mode === "manual" ? "warning" : "success"}>
                  {mode === "manual" ? "Manual" : "Auto"}
                </Badge>
                {cachedAt && (
                  <small className="text-muted">
                    Cache: {new Date(cachedAt).toLocaleString()}
                  </small>
                )}
                {updatedAt && (
                  <small className="text-muted">
                    Override updated: {new Date(updatedAt).toLocaleString()}
                  </small>
                )}
              </div>
              <small className="text-muted d-block mt-1">
                {mode === "auto"
                  ? "Values follow the live upstream schedule. Editing is locked — switch to Manual to override."
                  : isCustoms
                    ? "One Effective start Date applies to all currency rows in the public API. Auto refresh is paused until you switch back to Auto."
                    : "Edits save to Redis (served by public API) and database backup. Auto refresh is paused until you switch back to Auto."}
              </small>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="shadow-sm">
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table hover className="mb-0 align-middle">
              <thead className="table-light">
                <tr>
                  {fields.map((f) => (
                    <th key={f} style={{ minWidth: isCustoms && f.includes("Units") ? 180 : 110 }}>
                      {f}
                    </th>
                  ))}
                  {isCustoms && mode === "manual" && <th style={{ width: 80 }} />}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, ri) => (
                  <tr key={ri}>
                    {fields.map((f) => (
                      <td key={f}>
                        <Form.Control
                          size="sm"
                          type="text"
                          value={row[f] ?? ""}
                          onChange={(e) => updateCell(ri, f, e.target.value)}
                          readOnly={readOnly}
                          className={readOnly ? "bg-light" : ""}
                        />
                      </td>
                    ))}
                    {isCustoms && mode === "manual" && (
                      <td>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => removeCustomsRow(ri)}
                          disabled={rows.length <= 1}
                        >
                          ×
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Card.Body>
        {isCustoms && mode === "manual" && (
          <Card.Footer className="bg-white">
            <Button variant="outline-primary" size="sm" onClick={addCustomsRow}>
              Add currency row
            </Button>
          </Card.Footer>
        )}
      </Card>
    </Container>
  );
};

export const IndianCustomsRatesTab = () => (
  <ReferenceRatesAdminTab kind="customs" />
);

export const RbiReferenceRatesTab = () => (
  <ReferenceRatesAdminTab kind="rbi" />
);

export default ReferenceRatesAdminTab;
